/**
 * Step 3: Tenants
 * Select tenants for the lease with optional inline tenant creation
 */

import { useState, useEffect } from 'react';
import type { WizardState, CoSigner } from '../../../types/lease-generation';
import type { TenantWithDetails } from '../../../lib/api';
import { createTenant } from '../../../lib/api';
import { RequiredMark } from '../RequiredMark';
import { Button } from '../../../components/ui/button';
import { CoSignerEditor } from '../../../components/lease-inputs/CoSignerEditor';

interface Step3Props {
  state: WizardState;
  unitTenants: TenantWithDetails[];
  selectedTenantIds: string[];
  setSelectedTenantIds: (ids: string[]) => void;
  addCoSigner: (coSigner: CoSigner) => void;
  removeCoSigner: (index: number) => void;
}

export function Step3Tenants({
  state,
  unitTenants,
  selectedTenantIds,
  setSelectedTenantIds,
  addCoSigner,
  removeCoSigner,
}: Step3Props) {
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [localTenants, setLocalTenants] = useState<TenantWithDetails[]>(unitTenants);
  const [tenantForm, setTenantForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });

  // Sync when unitTenants prop arrives late (e.g. unit data loads after draft restore)
  useEffect(() => {
    setLocalTenants(prev => {
      const prevIds = new Set(prev.map(t => t.id));
      const incoming = unitTenants.filter(t => !prevIds.has(t.id));
      return incoming.length > 0 ? [...incoming, ...prev] : prev;
    });
  }, [unitTenants]);
  const [isSavingTenant, setIsSavingTenant] = useState(false);
  const [tenantError, setTenantError] = useState<string | null>(null);

  const { unitId, coSigners } = state;

  const toggleTenant = (tenantId: string) => {
    if (selectedTenantIds.includes(tenantId)) {
      setSelectedTenantIds(selectedTenantIds.filter(id => id !== tenantId));
    } else {
      setSelectedTenantIds([...selectedTenantIds, tenantId]);
    }
  };

  const handleAddTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantForm.email.trim() || !tenantForm.firstName.trim() || !tenantForm.lastName.trim()) {
      setTenantError('First name, last name, and email are required');
      return;
    }

    try {
      setIsSavingTenant(true);
      setTenantError(null);
      const newTenant = await createTenant(unitId, {
        firstName: tenantForm.firstName.trim(),
        lastName: tenantForm.lastName.trim(),
        email: tenantForm.email.trim(),
        phone: tenantForm.phone.trim() || undefined,
      });
      setLocalTenants(prev => [...prev, newTenant]);
      setSelectedTenantIds([...selectedTenantIds, newTenant.id]);
      setTenantForm({ firstName: '', lastName: '', email: '', phone: '' });
      setShowAddTenant(false);
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Failed to add tenant');
    } finally {
      setIsSavingTenant(false);
    }
  };

  const inputClass = 'w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
  const labelClass = 'block mb-1 text-sm font-medium text-foreground';

  return (
    <div className="flex flex-col gap-6" data-testid="step-tenants">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Select Tenants</h3>
        <p className="text-sm text-muted-foreground">Choose at least one tenant to include in this lease.</p>
      </div>

      {localTenants.length > 0 ? (
        <div className="flex flex-col gap-2">
          {localTenants.map((tenant) => (
            <label
              key={tenant.id}
              className="flex items-center gap-3 p-4 border border-border rounded-lg hover:bg-accent cursor-pointer"
            >
              <input
                type="checkbox"
                data-testid={`tenant-checkbox-${tenant.id}`}
                checked={selectedTenantIds.includes(tenant.id)}
                onChange={() => toggleTenant(tenant.id)}
                className="w-4 h-4 text-primary border-input rounded focus:ring-ring"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground">
                  {tenant.firstName && tenant.lastName
                    ? `${tenant.firstName} ${tenant.lastName}`
                    : tenant.email}
                </div>
                <div className="text-xs text-muted-foreground">{tenant.email}</div>
              </div>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                tenant.status === 'active' ? 'bg-success/10 text-success' :
                tenant.status === 'pending' ? 'bg-warning/10 text-warning' :
                'bg-muted text-muted-foreground'
              }`}>
                {tenant.status}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <div className="py-8 text-center bg-accent/50 rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">No tenants found for this unit.</p>
          <p className="text-xs text-muted-foreground mt-1">Add a tenant below to include them in this lease.</p>
        </div>
      )}

      {selectedTenantIds.length === 0 && (
        <p className="text-xs text-warning bg-warning/10 border border-warning/20 rounded px-3 py-2">
          At least one tenant must be selected to continue.
        </p>
      )}

      {/* Add New Tenant */}
      {!showAddTenant ? (
        <Button
          type="button"
          variant="outline"
          data-testid="add-tenant-button"
          onClick={() => setShowAddTenant(true)}
          className="w-full min-h-[44px] border-dashed text-primary border-primary/40 hover:bg-primary/5"
        >
          + Add New Tenant
        </Button>
      ) : (
        <div className="border border-border rounded-lg p-4 bg-accent/50">
          <h4 className="text-sm font-semibold text-foreground mb-3">Add New Tenant</h4>
          {tenantError && (
            <div className="p-3 mb-3 bg-destructive/5 border border-destructive/20 rounded text-sm text-destructive">
              {tenantError}
            </div>
          )}
          <form onSubmit={handleAddTenant} data-testid="add-tenant-form" className="flex flex-col gap-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="tenant-first-name" className={labelClass}>First Name <RequiredMark /></label>
                <input
                  id="tenant-first-name"
                  type="text"
                  data-testid="tenant-first-name-input"
                  value={tenantForm.firstName}
                  onChange={e => setTenantForm(f => ({ ...f, firstName: e.target.value }))}
                  className={inputClass}
                  required
                />
              </div>
              <div className="flex-1">
                <label htmlFor="tenant-last-name" className={labelClass}>Last Name <RequiredMark /></label>
                <input
                  id="tenant-last-name"
                  type="text"
                  data-testid="tenant-last-name-input"
                  value={tenantForm.lastName}
                  onChange={e => setTenantForm(f => ({ ...f, lastName: e.target.value }))}
                  className={inputClass}
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="tenant-email" className={labelClass}>Email <RequiredMark /></label>
              <input
                id="tenant-email"
                type="email"
                data-testid="tenant-email-input"
                value={tenantForm.email}
                onChange={e => setTenantForm(f => ({ ...f, email: e.target.value }))}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="tenant-phone" className={labelClass}>Phone</label>
              <input
                id="tenant-phone"
                type="tel"
                data-testid="tenant-phone-input"
                value={tenantForm.phone}
                onChange={e => setTenantForm(f => ({ ...f, phone: e.target.value }))}
                className={inputClass}
              />
            </div>
            <div className="flex gap-3 justify-end mt-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowAddTenant(false); setTenantError(null); }}
                className="min-h-[44px]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSavingTenant}
                className="min-h-[44px]"
              >
                {isSavingTenant ? 'Adding...' : 'Add Tenant'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <CoSignerEditor coSigners={coSigners} addCoSigner={addCoSigner} removeCoSigner={removeCoSigner} />
    </div>
  );
}
